/**
 * Página de Detalhes da Entrada - Gerencia itens de uma entrada específica
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus } from "lucide-react";
import { entradaAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { formatCurrency } from "@/lib/utils";
import { EntradaItemDialogRefatorado } from "@/components/entrada/EntradaItemDialogRefatorado";
import { EntradaItensTable } from "@/components/entrada/EntradaItensTable";
import { EntradaCompleta, ItemEntrada } from "@/types/entrada";

export default function EntradaDetalhes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [entrada, setEntrada] = useState<EntradaCompleta | null>(null);
  const [itens, setItens] = useState<ItemEntrada[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ItemEntrada | null>(null);

  useEffect(() => {
    if (id) {
      loadEntrada();
    }
  }, [id]);

  const loadEntrada = async () => {
    try {
      setIsLoading(true);
      const data = await entradaAdapter.get(id!);
      if (!data) {
        toast({
          title: "Entrada não encontrada",
          variant: "destructive",
        });
        navigate("/entrada");
        return;
      }
      setEntrada(data);
      const itensData = await entradaAdapter.getItens(id!);
      setItens(itensData);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar entrada",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setIsItemDialogOpen(true);
  };

  const handleEditItem = (item: ItemEntrada) => {
    setEditingItem(item);
    setIsItemDialogOpen(true);
  };

  const handleItemSuccess = () => {
    setIsItemDialogOpen(false);
    setEditingItem(null);
    loadEntrada();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!entrada) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <p className="text-muted-foreground">Entrada não encontrada</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/entrada")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Detalhes da Entrada</h1>
              <p className="text-sm text-muted-foreground">
                Gerencie os itens desta entrada de estoque
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddItem}>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Item
            </Button>
          </div>
        </div>

        {/* Informações da Entrada */}
        <Card>
          <CardHeader>
            <CardTitle>Informações da Entrada</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Tipo de Movimento</p>
                <p className="font-medium capitalize">{entrada.cabecalho.tipo_movimento.replace(/_/g, " ")}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Data</p>
                <p className="font-medium">{new Date(entrada.cabecalho.data_entrada).toLocaleDateString("pt-BR")}</p>
              </div>
              {entrada.cabecalho.fornecedor_nome && (
                <div>
                  <p className="text-sm text-muted-foreground">Fornecedor</p>
                  <p className="font-medium">{entrada.cabecalho.fornecedor_nome}</p>
                </div>
              )}
              {entrada.cabecalho.documento_numero && (
                <div>
                  <p className="text-sm text-muted-foreground">Documento</p>
                  <p className="font-medium">
                    {entrada.cabecalho.documento_tipo} {entrada.cabecalho.documento_numero}
                  </p>
                </div>
              )}
            </div>
            {entrada.cabecalho.observacao && (
              <>
                <Separator className="my-4" />
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Observação</p>
                  <p className="text-sm">{entrada.cabecalho.observacao}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Lista de Itens */}
        <Card>
          <CardHeader>
            <CardTitle>Itens da Entrada ({itens.length})</CardTitle>
            <CardDescription>
              Gerencie os itens desta entrada de estoque
            </CardDescription>
          </CardHeader>
          <CardContent>
            {itens.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">Nenhum item adicionado ainda</p>
                <Button onClick={handleAddItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Primeiro Item
                </Button>
              </div>
            ) : (
              <>
                <EntradaItensTable
                  itens={itens}
                  onEdit={handleEditItem}
                  onRefresh={loadEntrada}
                  readOnly={false}
                />
                <Separator className="my-4" />
                <div className="flex justify-end">
                  <div className="space-y-2 min-w-[200px]">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Outras Despesas:</span>
                      <span>{formatCurrency(entrada.totais.outras_despesas_total || 0)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>{formatCurrency(entrada.totais.total_geral_documento)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Item */}
      <EntradaItemDialogRefatorado
        open={isItemDialogOpen}
        onOpenChange={setIsItemDialogOpen}
        entradaId={id!}
        item={editingItem}
        onSuccess={handleItemSuccess}
      />
    </Layout>
  );
}
